<template>
  <div class="disbursement-configs-list">
    <KTCodePreview v-bind:title="title">
      <template v-slot:toolbar>
        <div class="d-flex gap-2">
          <router-link
            v-if="checkPermission('DISBURSEMENT_CONFIG_INSERT')"
            :to="{ name: 'add-disbursement-config' }"
          >
            <b-button variant="primary" size="sm" class="font-weight-bolder">
              <i style="font-size: 1rem" class="flaticon2-add-1"></i> Thêm mới
            </b-button>
          </router-link>
        </div>
      </template>
      <template v-slot:preview>
        <!-- Search bar -->
        <b-row class="mb-4">
          <b-col md="3">
            <b-input
              size="sm"
              placeholder="Tìm theo tên..."
              v-model="draftSearchQuery"
              v-on:keyup.enter="onFilter"
              autocomplete="off"
            />
          </b-col>

          <!-- Filter loại giải ngân -->
          <b-col md="3">
            <b-form-select
              v-model="draftFilterParentId"
              :options="parentFilterOptions"
              size="sm"
              class="select-style"
            >
              <template #first>
                <b-form-select-option :value="null">-- Tất cả loại giải ngân --</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>

          <b-col md="2">
            <b-button size="sm" variant="secondary" @click="clearSearch">
              <i class="fas fa-redo pr-0"></i> Làm mới
            </b-button>
          </b-col>
        </b-row>

        <b-row class="mb-4">
          <b-col cols="1">
            <b-button
              size="sm"
              variant="primary"
              style="fontweight: 600; width: 70px"
              @click="onFilter"
              >Lọc</b-button
            >
          </b-col>
        </b-row>

        <!-- Loading skeleton -->
        <div v-if="onLoading">
          <vcl-table :speed="5" :animate="true" :columns="5"></vcl-table>
        </div>

        <!-- Tree table -->
        <div v-else>
          <div v-if="!filteredTree.length" class="text-center text-muted py-5">
            <i class="fas fa-inbox fa-2x mb-2"></i>
            <p>Không có cấu hình nào phù hợp.</p>
          </div>

          <table v-else class="table table-bordered table-hover table-sm config-tree-table">
            <thead>
              <tr>
                <th style="width: 5%">ID</th>
                <th style="width: 28%">Tên</th>
                <th style="width: 12%">Loại</th>
                <th style="width: 20%">Ghi chú</th>
                <th style="width: 10%">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="parent in filteredTree">
                <!-- Parent row -->
                <tr :key="`parent-${parent.id}`" class="parent-row">
                  <td class="text-center"><strong>{{ parent.id }}</strong></td>
                  <td>
                    <span class="parent-label">
                      <i class="fas fa-layer-group text-primary mr-1"></i>
                      <strong>{{ parent.name }}</strong>
                    </span>
                  </td>
                  <td>
                    <b-badge variant="primary">Loại giải ngân</b-badge>
                  </td>
                  <td>{{ parent.notes || '' }}</td>
                  <td class="text-center">
                    <b-dropdown size="sm" no-caret right lazy>
                      <template slot="button-content">
                        <i style="font-size: 1rem" class="flaticon2-settings pr-0"></i>
                      </template>
                      <b-dropdown-item @click="addChildForParent(parent.id)">
                        <span style="color: #3f4254; font-size: 12px">
                          <i style="font-size: 1rem; color: #1bc5bd" class="flaticon2-add-1"></i>
                          &nbsp; Thêm nhà giải ngân
                        </span>
                      </b-dropdown-item>
                      <b-dropdown-item @click="showDeleteAlert(parent.id, parent.name)">
                        <span style="color: #3f4254; font-size: 12px">
                          <i style="font-size: 1rem; color: #d33" class="flaticon2-rubbish-bin-delete-button"></i>
                          &nbsp; Xoá
                        </span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </td>
                </tr>
                <!-- Child rows -->
                <tr
                  v-for="child in parent.childs"
                  :key="`child-${child.id}`"
                  class="child-row"
                >
                  <td class="text-center">{{ child.id }}</td>
                  <td>
                    <span class="child-label">
                      <i class="fas fa-building text-info mr-1"></i>
                      {{ child.name }}
                    </span>
                  </td>
                  <td>
                    <b-badge variant="info">Nhà giải ngân</b-badge>
                  </td>
                  <td class="small">{{ child.notes || '' }}</td>
                  <td class="text-center">
                    <b-dropdown size="sm" no-caret right lazy>
                      <template slot="button-content">
                        <i style="font-size: 1rem" class="flaticon2-settings pr-0"></i>
                      </template>
                      <b-dropdown-item
                        v-if="checkPermission('DISBURSEMENT_CONFIG_UPDATE')"
                        @click="editConfig(child.id)"
                      >
                        <span style="color: #3f4254; font-size: 12px">
                          <i style="font-size: 1rem" class="flaticon2-pen"></i>
                          &nbsp; Sửa
                        </span>
                      </b-dropdown-item>
                      <b-dropdown-item @click="showDeleteAlert(child.id, child.name)">
                        <span style="color: #3f4254; font-size: 12px">
                          <i style="font-size: 1rem; color: #d33" class="flaticon2-rubbish-bin-delete-button"></i>
                          &nbsp; Xoá
                        </span>
                      </b-dropdown-item>
                    </b-dropdown>
                  </td>
                </tr>
                <!-- Empty children placeholder -->
                <tr v-if="!parent.childs || !parent.childs.length" :key="`empty-${parent.id}`" class="child-row">
                  <td colspan="5" class="text-muted text-center small py-2">
                    <i>Chưa có nhà giải ngân nào —</i>
                    <a href="#" class="ml-1" @click.prevent="addChildForParent(parent.id)">Thêm ngay</a>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { VclTable } from 'vue-content-loading';
import { makeToastFaile, makeToastSuccess } from '@/utils/common';
import { TIME_OUT } from '@/utils/constants';
import Swal from 'sweetalert2';
import permissionMixin from '@/view/mixins/permission.mixin';

export default {
  name: 'ListDisbursementConfigs',
  mixins: [permissionMixin],
  components: {
    KTCodePreview,
    VclTable,
  },
  data() {
    return {
      title: 'Cấu hình nhà giải ngân',
      treeData: [],      // toàn bộ tree từ API
      onLoading: false,
      // draft: người dùng đang nhập/chọn (chưa áp dụng)
      draftSearchQuery: '',
      draftFilterParentId: null,
      // applied: chỉ thay đổi khi bấm "Lọc" (hoặc Enter)
      searchQuery: '',
      filterParentId: null,   // lọc theo loại giải ngân (parent id)
    };
  },
  computed: {
    // Danh sách options cho dropdown filter loại giải ngân
    parentFilterOptions() {
      return this.treeData.map((p) => ({ value: p.id, text: p.name }));
    },
    // Tree đã được lọc theo filterParentId + searchQuery (client-side filter)
    filteredTree() {
      let result = this.treeData;

      // Lọc theo loại giải ngân
      if (this.filterParentId !== null) {
        result = result.filter((p) => p.id === this.filterParentId);
      }

      // Lọc theo tên (search)
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        result = result
          .map((p) => {
            const parentMatch = p.name.toLowerCase().includes(q);
            const matchedChilds = (p.childs || []).filter((c) =>
              c.name.toLowerCase().includes(q),
            );
            if (parentMatch || matchedChilds.length) {
              return { ...p, childs: parentMatch ? p.childs : matchedChilds };
            }
            return null;
          })
          .filter(Boolean);
      }

      return result;
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Giải ngân công nợ', route: '/disbursement' },
      { title: this.title },
    ]);
  },
  created() {
    this.fetchConfigs();
  },
  methods: {
    onFilter() {
      this.searchQuery = this.draftSearchQuery;
      this.filterParentId = this.draftFilterParentId;
    },
    clearSearch() {
      this.draftSearchQuery = '';
      this.draftFilterParentId = null;
      this.searchQuery = '';
      this.filterParentId = null;
      this.fetchConfigs();
    },
    fetchConfigs() {
      if (this.onLoading) return;
      this.onLoading = true;
      // Fetch tất cả, filter client-side (tree nhỏ nên OK)
      ApiService.query('v1/disbursement-configs', {}, { timeout: TIME_OUT })
        .then((response) => {
          this.treeData = response.data.data || [];
        })
        .catch((error) => {
          makeToastFaile(error.response?.data?.message || error.message || 'Lỗi tải danh sách');
        })
        .finally(() => {
          this.onLoading = false;
        });
    },
    editConfig(id) {
      this.$router.push({ name: 'update-disbursement-config', params: { id: String(id) } });
    },
    addChildForParent(parentId) {
      this.$router.push({ name: 'add-disbursement-config', query: { parentId: String(parentId) } });
    },
    deleteConfig(id) {
      ApiService.delete(`v1/disbursement-configs/${id}`)
        .then((response) => {
          makeToastSuccess(response.data.message || 'Xoá thành công');
          this.fetchConfigs();
        })
        .catch((error) => {
          makeToastFaile(error.response?.data?.message || error.message || 'Lỗi xoá');
        });
    },
    showDeleteAlert(id, name) {
      Swal.fire({
        title: 'Xoá cấu hình!',
        html: `Bạn có chắc muốn xoá <b>"${name}"</b> không?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xoá',
        cancelButtonText: '<span style="color:#464E5F">Huỷ</span>',
        reverseButtons: true,
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' },
      }).then((result) => {
        if (result.value) {
          this.deleteConfig(id);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.disbursement-configs-list {
  .config-tree-table {
    font-size: 13px;

    thead th {
      background-color: #f3f6f9;
      font-weight: 600;
      vertical-align: middle;
    }

    .parent-row {
      background-color: #f8f9fa;

      td {
        vertical-align: middle;
        border-bottom: 2px solid #dee2e6;
      }

      .parent-label {
        font-size: 13.5px;
      }
    }

    .child-row {
      td {
        vertical-align: middle;
        padding-left: 1.5rem;
      }

      .child-label {
        padding-left: 1.25rem;
        display: inline-block;
      }
    }
  }

  .gap-2 {
    gap: 0.5rem;
  }
}
</style>
